import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { playNewPendingSound } from '../utils/orderAlertSound';

/** Suena cuando llega un pedido nuevo pendiente de aprobar (sin trabar la app). */
export function usePendingOrderAlert(pin, { refreshKey = 0, enabled = true, onNewPending } = {}) {
  const knownIdsRef = useRef(null);
  const onNewRef = useRef(onNewPending);
  onNewRef.current = onNewPending;
  const inFlightRef = useRef(false);
  const remindersRef = useRef(0);
  const [pendientesCount, setPendientesCount] = useState(0);

  const check = useCallback(async () => {
    if (!enabled || !pin || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await api.getPedidos({ estado: 'pendiente', limit: 100 });
      const list = res.data || [];
      const ids = new Set(list.map((p) => p.id));
      setPendientesCount(list.length);

      if (knownIdsRef.current !== null) {
        const nuevos = [];
        for (const id of ids) {
          if (!knownIdsRef.current.has(id)) {
            const pedido = list.find((p) => p.id === id);
            if (pedido) nuevos.push(pedido);
          }
        }
        if (nuevos.length) {
          remindersRef.current = 0;
          playNewPendingSound({
            title: nuevos.length > 1 ? `${nuevos.length} pedidos` : nuevos[0].numero || 'Pedido',
          });
          onNewRef.current?.(nuevos[0], nuevos.length);
        } else if (
          list.length > 0 &&
          document.visibilityState === 'hidden' &&
          remindersRef.current < 2
        ) {
          // Máximo 2 recordatorios y solo si la pestaña está en segundo plano
          remindersRef.current += 1;
          playNewPendingSound({ title: `${list.length} pendiente(s)`, short: true });
        }
        if (!list.length) remindersRef.current = 0;
      }
      knownIdsRef.current = ids;
    } catch {
      /* no tumbar la app por un fallo de red */
    } finally {
      inFlightRef.current = false;
    }
  }, [pin, enabled]);

  useEffect(() => {
    check();
    const t = setInterval(check, 45000);
    const onVis = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onOnline = () => check();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
    };
  }, [check, refreshKey]);

  return pendientesCount;
}
